import React, { useState, useEffect } from 'react';
import { Employee, Payment, EmployerSettings } from './types/Employee';
import { generateEmployeeDataPDF } from './employeePDF';
import { generateEmploymentContractPDF } from './contractPDF';
import './EmployeeEditor.css';

// Get base path for API calls (app is at domain root)
const getBasePath = () => {
  return '/databaseOps/';
};

// Get authentication headers with JWT token
const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('auth_token');
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

// API functions
async function loadAllEmployees(showEnded: boolean = false): Promise<Employee[]> {
  const url = `${getBasePath()}getListOfWorkers.php?showEnded=${showEnded}`;
  const response = await fetch(url, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch employees: ${response.statusText}`);
  }

  return response.json();
}

async function loadEmployeeDetails(id: number): Promise<Employee> {
  const response = await fetch(`${getBasePath()}getEmployeeDetails.php?id=${id}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch employee details: ${response.statusText}`);
  }

  return response.json();
}

async function saveEmployee(employee: Employee): Promise<any> {
  const response = await fetch(`${getBasePath()}saveEmployee.php`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(employee),
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Failed to save employee');
  }

  return result;
}

async function deleteEmployee(id: number): Promise<any> {
  const response = await fetch(`${getBasePath()}deleteEmployee.php`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ id }),
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Failed to delete employee');
  }

  return result;
}

async function loadEmployerSettings(): Promise<EmployerSettings> {
  const response = await fetch(`${getBasePath()}getEmployerSettings.php`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch employer settings: ${response.statusText}`);
  }

  return response.json();
}

async function loadEmployeePayments(employeeId: number): Promise<Payment[]> {
  const response = await fetch(`${getBasePath()}getEmployeePayments.php?employee_id=${employeeId}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch payments: ${response.statusText}`);
  }

  return response.json();
}

async function savePayment(payment: Payment): Promise<any> {
  const response = await fetch(`${getBasePath()}savePayment.php`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payment),
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Failed to save payment');
  }

  return result;
}

async function deletePayment(id: number): Promise<any> {
  const response = await fetch(`${getBasePath()}deletePayment.php`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ id }),
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Failed to delete payment');
  }

  return result;
}

function EmployeeEditor() {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [showPayments, setShowPayments] = useState(false);
  const [showEnded, setShowEnded] = useState(false);

  useEffect(() => {
    loadEmployees();
  }, [showEnded]);

  const loadEmployees = async () => {
    try {
      const data = await loadAllEmployees(showEnded);
      setEmployees(data);
    } catch (error: any) {
      console.error('Error loading employees:', error);
      alert(`Fehler beim Laden der Mitarbeiter:\n\n${error.message}`);
    }
  };

  const handleEdit = async (employee: Employee) => {
    try {
      const details = await loadEmployeeDetails(employee.ID!);
      setEditingEmployee(details);
      setIsCreating(false);
      setShowPayments(false);
    } catch (error: any) {
      console.error('Error loading employee details:', error);
      alert(`Fehler beim Laden der Mitarbeiterdaten:\n\n${error.message}`);
    }
  };

  const handleCreate = () => {
    const newEmployee: Employee = {
      Name: '',
      first_name: '',
      last_name: '',
      EMail: '',
      Admin: false,
      mobile_phone: '',
      secondary_phone: '',
      street: '',
      postal_code: '',
      city: '',
      bank_name: '',
      iban: '',
      bic: '',
      account_holder: '',
      health_insurance: '',
      social_security_number: '',
      tax_id: '',
      employment_start_date: new Date().toISOString().split('T')[0],
      employment_end_date: null,
      vacation_days_per_year: 28,
      contracted_hours_per_month: 0,
      hourly_rate: 13.00,
      deleted: false,
    };
    setEditingEmployee(newEmployee);
    setIsCreating(true);
    setShowPayments(false);
  };

  const handleSave = async () => {
    if (!editingEmployee) return;

    // Validation
    if (!editingEmployee.first_name?.trim() && !editingEmployee.last_name?.trim()) {
      alert('Bitte geben Sie mindestens Vor- oder Nachnamen ein.');
      return;
    }

    if (!editingEmployee.EMail.trim()) {
      alert('Bitte geben Sie eine E-Mail-Adresse ein.');
      return;
    }

    // Auto-generate Name field from first_name and last_name for backward compatibility
    const updatedEmployee = {
      ...editingEmployee,
      Name: `${editingEmployee.first_name?.trim() || ''} ${editingEmployee.last_name?.trim() || ''}`.trim()
    };

    setSaveStatus('saving');

    try {
      const result = await saveEmployee(updatedEmployee);

      // Reload employees list
      await loadEmployees();

      setSaveStatus('success');
      await new Promise(resolve => setTimeout(resolve, 1500));

      setSaveStatus('idle');
      setEditingEmployee(null);
      setIsCreating(false);

      alert(result.message);
    } catch (error: any) {
      console.error('Error saving employee:', error);
      setSaveStatus('error');
      alert(`Fehler beim Speichern:\n\n${error.message}`);
      await new Promise(resolve => setTimeout(resolve, 2000));
      setSaveStatus('idle');
    }
  };

  const handleDelete = async () => {
    if (!editingEmployee || !editingEmployee.ID) return;

    if (!window.confirm(
      `Möchten Sie den Mitarbeiter "${editingEmployee.Name}" wirklich löschen?\n\n` +
      `Dies setzt den Status auf "gelöscht" und beendet das Arbeitsverhältnis.`
    )) {
      return;
    }

    try {
      await deleteEmployee(editingEmployee.ID);
      await loadEmployees();
      setEditingEmployee(null);
      alert('Mitarbeiter wurde gelöscht.');
    } catch (error: any) {
      console.error('Error deleting employee:', error);
      alert(`Fehler beim Löschen:\n\n${error.message}`);
    }
  };

  const handleCancel = () => {
    setEditingEmployee(null);
    setIsCreating(false);
    setShowPayments(false);
  };

  const handleGenerateContract = async () => {
    if (!editingEmployee) return;

    try {
      // Load employer settings
      const employerSettings = await loadEmployerSettings();

      // Generate contract PDF
      generateEmploymentContractPDF(editingEmployee, employerSettings);
    } catch (error: any) {
      console.error('Error generating contract:', error);
      alert(`Fehler beim Erstellen des Arbeitsvertrags:\n\n${error.message}`);
    }
  };

  const handleFieldChange = (field: keyof Employee, value: any) => {
    if (!editingEmployee) return;
    setEditingEmployee({
      ...editingEmployee,
      [field]: value,
    });
  };

  return (
    <div className="employee-editor-container">
      <div className="employee-header">
        <h2>Mitarbeiter-Verwaltung</h2>
        <button className="btn-create-employee" onClick={handleCreate}>
          + Neuer Mitarbeiter
        </button>
      </div>

      {!editingEmployee ? (
        <>
          <div className="filter-controls">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={showEnded}
                onChange={(e) => setShowEnded(e.target.checked)}
              />
              Beendete Arbeitsverhältnisse anzeigen
            </label>
          </div>
          <div className="employees-list">
            {employees.map((employee) => (
            <div
              key={employee.ID}
              className={`employee-card ${employee.deleted ? 'deleted' : ''}`}
              onClick={() => handleEdit(employee)}
            >
              <h3>{employee.Name}</h3>
              <p>{employee.EMail}</p>
              {employee.deleted && <span className="badge-deleted">Gelöscht</span>}
              {employee.Admin && <span className="badge-admin">Admin</span>}
            </div>
          ))}
          </div>
        </>
      ) : (
        <div className="employee-edit-form">
          <h3>{isCreating ? 'Neuer Mitarbeiter' : `Mitarbeiter bearbeiten: ${editingEmployee.Name}`}</h3>

          {/* Personal Information */}
          <div className="form-section">
            <h4>Persönliche Daten</h4>
            <div className="form-grid">
              <div className="form-field">
                <label>Vorname *</label>
                <input
                  type="text"
                  value={editingEmployee.first_name || ''}
                  onChange={(e) => handleFieldChange('first_name', e.target.value)}
                />
              </div>

              <div className="form-field">
                <label>Nachname *</label>
                <input
                  type="text"
                  value={editingEmployee.last_name || ''}
                  onChange={(e) => handleFieldChange('last_name', e.target.value)}
                />
              </div>

              <div className="form-field">
                <label>E-Mail *</label>
                <input
                  type="email"
                  value={editingEmployee.EMail}
                  onChange={(e) => handleFieldChange('EMail', e.target.value)}
                />
              </div>

              <div className="form-field">
                <label>Mobiltelefon</label>
                <input
                  type="tel"
                  value={editingEmployee.mobile_phone || ''}
                  onChange={(e) => handleFieldChange('mobile_phone', e.target.value)}
                />
              </div>

              <div className="form-field">
                <label>Zweittelefon</label>
                <input
                  type="tel"
                  value={editingEmployee.secondary_phone || ''}
                  onChange={(e) => handleFieldChange('secondary_phone', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="form-section">
            <h4>Adresse</h4>
            <div className="form-grid">
              <div className="form-field full-width">
                <label>Straße und Hausnummer</label>
                <input
                  type="text"
                  value={editingEmployee.street || ''}
                  onChange={(e) => handleFieldChange('street', e.target.value)}
                  placeholder="z.B. Musterstraße 123"
                />
              </div>

              <div className="form-field">
                <label>PLZ</label>
                <input
                  type="text"
                  value={editingEmployee.postal_code || ''}
                  onChange={(e) => handleFieldChange('postal_code', e.target.value)}
                  placeholder="12345"
                />
              </div>

              <div className="form-field">
                <label>Ort</label>
                <input
                  type="text"
                  value={editingEmployee.city || ''}
                  onChange={(e) => handleFieldChange('city', e.target.value)}
                  placeholder="Musterstadt"
                />
              </div>
            </div>
          </div>

          {/* Bank Account Information */}
          <div className="form-section">
            <h4>Bankverbindung</h4>
            <div className="form-grid">
              <div className="form-field">
                <label>Bank</label>
                <input
                  type="text"
                  value={editingEmployee.bank_name || ''}
                  onChange={(e) => handleFieldChange('bank_name', e.target.value)}
                  placeholder="z.B. Sparkasse"
                />
              </div>

              <div className="form-field">
                <label>IBAN</label>
                <input
                  type="text"
                  value={editingEmployee.iban || ''}
                  onChange={(e) => handleFieldChange('iban', e.target.value)}
                  placeholder="DE89 3704 0044 0532 0130 00"
                />
              </div>

              <div className="form-field">
                <label>BIC (optional)</label>
                <input
                  type="text"
                  value={editingEmployee.bic || ''}
                  onChange={(e) => handleFieldChange('bic', e.target.value)}
                  placeholder="COBADEFFXXX"
                />
              </div>

              <div className="form-field">
                <label>Kontoinhaber (falls abweichend)</label>
                <input
                  type="text"
                  value={editingEmployee.account_holder || ''}
                  onChange={(e) => handleFieldChange('account_holder', e.target.value)}
                  placeholder="Leer lassen, wenn gleich Name"
                />
              </div>
            </div>
          </div>

          {/* Official Employment Data */}
          <div className="form-section">
            <h4>Offizielle Daten</h4>
            <div className="form-grid">
              <div className="form-field">
                <label>Krankenkasse</label>
                <input
                  type="text"
                  value={editingEmployee.health_insurance || ''}
                  onChange={(e) => handleFieldChange('health_insurance', e.target.value)}
                />
              </div>

              <div className="form-field">
                <label>Sozialversicherungsnummer</label>
                <input
                  type="text"
                  value={editingEmployee.social_security_number || ''}
                  onChange={(e) => handleFieldChange('social_security_number', e.target.value)}
                />
              </div>

              <div className="form-field">
                <label>Steuer-ID</label>
                <input
                  type="text"
                  value={editingEmployee.tax_id || ''}
                  onChange={(e) => handleFieldChange('tax_id', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Employment Dates */}
          <div className="form-section">
            <h4>Beschäftigung</h4>
            <div className="form-grid">
              <div className="form-field">
                <label>Beginn der Beschäftigung</label>
                <input
                  type="date"
                  value={editingEmployee.employment_start_date || ''}
                  onChange={(e) => handleFieldChange('employment_start_date', e.target.value)}
                />
              </div>

              <div className="form-field">
                <label>Ende der Beschäftigung</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="date"
                    value={editingEmployee.employment_end_date || ''}
                    onChange={(e) => handleFieldChange('employment_end_date', e.target.value || null)}
                    style={{ flex: 1 }}
                  />
                  {editingEmployee.employment_end_date && (
                    <button
                      type="button"
                      onClick={() => handleFieldChange('employment_end_date', null)}
                      style={{
                        padding: '4px 8px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                      }}
                      title="Datum löschen"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Contract Terms */}
          <div className="form-section">
            <h4>Vertragsdaten</h4>
            <div className="form-grid">
              <div className="form-field">
                <label>Urlaubstage pro Jahr</label>
                <input
                  type="number"
                  min="0"
                  value={editingEmployee.vacation_days_per_year || 0}
                  onChange={(e) => handleFieldChange('vacation_days_per_year', parseInt(e.target.value) || 0)}
                />
              </div>

              <div className="form-field">
                <label>Sollstunden pro Monat</label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={editingEmployee.contracted_hours_per_month || 0}
                  onChange={(e) => handleFieldChange('contracted_hours_per_month', parseFloat(e.target.value) || 0)}
                />
              </div>

              <div className="form-field">
                <label>Stundenlohn (€)</label>
                <input
                  type="number"
                  min="0"
                  step="0.50"
                  value={editingEmployee.hourly_rate || 0}
                  onChange={(e) => handleFieldChange('hourly_rate', parseFloat(e.target.value) || 0)}
                />
              </div>

              <div className="form-field">
                <label>
                  <input
                    type="checkbox"
                    checked={editingEmployee.Admin || false}
                    onChange={(e) => handleFieldChange('Admin', e.target.checked)}
                  />
                  Administrator
                </label>
              </div>
            </div>
          </div>

          {/* Contract Details */}
          <div className="form-section">
            <h4>Vertragsdetails</h4>
            <div className="form-grid">
              <div className="form-field">
                <label>Geschlecht</label>
                <select
                  value={editingEmployee.gender || ''}
                  onChange={(e) => handleFieldChange('gender', e.target.value || null)}
                >
                  <option value="">Nicht angegeben</option>
                  <option value="m">Männlich</option>
                  <option value="f">Weiblich</option>
                  <option value="d">Divers</option>
                </select>
              </div>

              <div className="form-field">
                <label>Berufsbezeichnung</label>
                <input
                  type="text"
                  value={editingEmployee.job_title || ''}
                  onChange={(e) => handleFieldChange('job_title', e.target.value)}
                  placeholder="z.B. Stallhelfer, Reitlehrer"
                />
              </div>

              <div className="form-field">
                <label>Vertragsart</label>
                <select
                  value={editingEmployee.contract_type || 'unbefristet'}
                  onChange={(e) => handleFieldChange('contract_type', e.target.value)}
                >
                  <option value="unbefristet">Unbefristet</option>
                  <option value="befristet">Befristet</option>
                </select>
              </div>

              <div className="form-field">
                <label>Arbeitstage pro Woche</label>
                <input
                  type="number"
                  min="0"
                  max="7"
                  step="0.5"
                  value={editingEmployee.working_days_per_week || ''}
                  onChange={(e) => handleFieldChange('working_days_per_week', parseFloat(e.target.value) || null)}
                  placeholder="z.B. 5"
                />
              </div>

              <div className="form-field">
                <label>Stunden pro Tag</label>
                <input
                  type="number"
                  min="0"
                  max="24"
                  step="0.25"
                  value={editingEmployee.hours_per_day || ''}
                  onChange={(e) => handleFieldChange('hours_per_day', parseFloat(e.target.value) || null)}
                  placeholder="z.B. 4"
                />
              </div>

              <div className="form-field">
                <label>Kommentar zum Stundenlohn</label>
                <input
                  type="text"
                  value={editingEmployee.hourly_rate_comment || ''}
                  onChange={(e) => handleFieldChange('hourly_rate_comment', e.target.value)}
                  placeholder="z.B. Mindestlohn von derzeit"
                />
              </div>

              <div className="form-field full-width">
                <label>Arbeitsort</label>
                <input
                  type="text"
                  value={editingEmployee.workplace_location || ''}
                  onChange={(e) => handleFieldChange('workplace_location', e.target.value)}
                  placeholder="z.B. Fockbek, Reitanlage am Barris, Barriser Weg 60"
                />
              </div>

              <div className="form-field full-width">
                <label>Tätigkeitsbeschreibung</label>
                <textarea
                  value={editingEmployee.job_description || ''}
                  onChange={(e) => handleFieldChange('job_description', e.target.value)}
                  placeholder="Beschreibung der Aufgaben für den Arbeitsvertrag..."
                  rows={4}
                />
              </div>
            </div>
          </div>

          {/* Hours Balance */}
          {!isCreating && editingEmployee.hours_balance && (
            <div className="form-section hours-balance-section">
              <h4>Stundenkonto</h4>
              <div className="hours-balance-grid">
                <div className="balance-item">
                  <span className="label">Gearbeitete Stunden:</span>
                  <span className="value">{editingEmployee.hours_balance.total_hours_worked.toFixed(2)} h</span>
                </div>
                <div className="balance-item">
                  <span className="label">Wert der Arbeit:</span>
                  <span className="value">
                    {editingEmployee.hours_balance.total_worked_value.toLocaleString('de-DE', {
                      style: 'currency',
                      currency: 'EUR',
                    })}
                  </span>
                </div>
                <div className="balance-item">
                  <span className="label">Gesamt bezahlt:</span>
                  <span className="value">
                    {editingEmployee.hours_balance.total_amount_paid.toLocaleString('de-DE', {
                      style: 'currency',
                      currency: 'EUR',
                    })}
                  </span>
                </div>
                <div className={`balance-item ${editingEmployee.hours_balance.balance_eur > 0 ? 'positive' : editingEmployee.hours_balance.balance_eur < 0 ? 'negative' : ''}`}>
                  <span className="label">Saldo:</span>
                  <span className="value">
                    {editingEmployee.hours_balance.balance_eur > 0 ? '+' : ''}
                    {editingEmployee.hours_balance.balance_eur.toLocaleString('de-DE', {
                      style: 'currency',
                      currency: 'EUR',
                    })}
                  </span>
                </div>
              </div>
              <button className="btn-manage-payments" onClick={() => setShowPayments(!showPayments)}>
                {showPayments ? 'Zahlungen ausblenden' : 'Zahlungen verwalten'}
              </button>
            </div>
          )}

          {/* Payments Section */}
          {showPayments && !isCreating && editingEmployee.ID && (
            <PaymentsManager employeeId={editingEmployee.ID} onUpdate={async () => {
              const details = await loadEmployeeDetails(editingEmployee.ID!);
              setEditingEmployee(details);
            }} />
          )}

          {/* Action Buttons */}
          <div className="form-actions">
            <button className="btn-cancel" onClick={handleCancel}>
              Abbrechen
            </button>
            {!isCreating && (
              <button className="btn-delete" onClick={handleDelete}>
                Löschen
              </button>
            )}
            {!isCreating && (
              <button className="btn-export-pdf" onClick={() => generateEmployeeDataPDF(editingEmployee)}>
                PDF für Steuerberater
              </button>
            )}
            {!isCreating && (
              <button className="btn-generate-contract" onClick={handleGenerateContract}>
                Arbeitsvertrag erstellen
              </button>
            )}
            <button
              className={`btn-save ${saveStatus === 'saving' ? 'saving' : ''} ${saveStatus === 'success' ? 'success' : ''} ${saveStatus === 'error' ? 'error' : ''}`}
              onClick={handleSave}
              disabled={saveStatus === 'saving'}
            >
              {saveStatus === 'saving' ? 'Speichert...' : saveStatus === 'success' ? '✓ Gespeichert' : 'Speichern'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Payments Manager Component
function PaymentsManager({ employeeId, onUpdate }: { employeeId: number; onUpdate: () => void }) {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [newPayment, setNewPayment] = useState<Payment>({
    employee_id: employeeId,
    payment_date: new Date().toISOString().split('T')[0],
    amount_eur: 0,
    worked_value: null,
    period_start: null,
    period_end: null,
    notes: '',
  });
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    loadPayments();
  }, [employeeId]);

  const loadPayments = async () => {
    try {
      const data = await loadEmployeePayments(employeeId);
      setPayments(data);
    } catch (error: any) {
      console.error('Error loading payments:', error);
    }
  };

  const handleAddPayment = async () => {
    if (newPayment.amount_eur <= 0) {
      alert('Bitte geben Sie einen gültigen Betrag ein.');
      return;
    }

    try {
      await savePayment(newPayment);
      await loadPayments();
      await onUpdate();
      setShowAddForm(false);
      setNewPayment({
        employee_id: employeeId,
        payment_date: new Date().toISOString().split('T')[0],
        amount_eur: 0,
        worked_value: null,
        period_start: null,
        period_end: null,
        notes: '',
      });
    } catch (error: any) {
      console.error('Error saving payment:', error);
      alert(`Fehler beim Speichern der Zahlung:\n\n${error.message}`);
    }
  };

  const handleDeletePayment = async (id: number) => {
    if (!window.confirm('Möchten Sie diese Zahlung wirklich löschen?')) {
      return;
    }

    try {
      await deletePayment(id);
      await loadPayments();
      await onUpdate();
    } catch (error: any) {
      console.error('Error deleting payment:', error);
      alert(`Fehler beim Löschen:\n\n${error.message}`);
    }
  };

  return (
    <div className="payments-manager">
      <h4>Zahlungshistorie</h4>

      <button className="btn-add-payment" onClick={() => setShowAddForm(!showAddForm)}>
        {showAddForm ? '- Abbrechen' : '+ Neue Zahlung'}
      </button>

      {showAddForm && (
        <div className="payment-add-form">
          <div className="form-grid">
            <div className="form-field">
              <label>Zahlungsdatum</label>
              <input
                type="date"
                value={newPayment.payment_date}
                onChange={(e) => setNewPayment({ ...newPayment, payment_date: e.target.value })}
              />
            </div>

            <div className="form-field">
              <label>Betrag (€)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={newPayment.amount_eur}
                onChange={(e) => setNewPayment({ ...newPayment, amount_eur: parseFloat(e.target.value) || 0 })}
              />
            </div>


            <div className="form-field">
              <label>Zeitraum von (optional)</label>
              <input
                type="date"
                value={newPayment.period_start || ''}
                onChange={(e) => setNewPayment({ ...newPayment, period_start: e.target.value || null })}
              />
            </div>

            <div className="form-field">
              <label>Zeitraum bis (optional)</label>
              <input
                type="date"
                value={newPayment.period_end || ''}
                onChange={(e) => setNewPayment({ ...newPayment, period_end: e.target.value || null })}
              />
            </div>

            <div className="form-field full-width">
              <label>Notizen (optional)</label>
              <textarea
                value={newPayment.notes || ''}
                onChange={(e) => setNewPayment({ ...newPayment, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>

          <button className="btn-save-payment" onClick={handleAddPayment}>
            Zahlung speichern
          </button>
        </div>
      )}

      <div className="payments-list">
        {payments.length === 0 ? (
          <p className="no-payments">Keine Zahlungen vorhanden</p>
        ) : (
          payments.map((payment) => (
            <div key={payment.id} className="payment-card">
              <div className="payment-header">
                <span className="payment-date">
                  {new Date(payment.payment_date).toLocaleDateString('de-DE')}
                </span>
                <button className="btn-delete-payment" onClick={() => handleDeletePayment(payment.id!)}>
                  ✕
                </button>
              </div>
              <div className="payment-details">
                <div>
                  <strong>{payment.amount_eur.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</strong>
                </div>
                {payment.period_start && payment.period_end && (
                  <div className="payment-period">
                    Zeitraum: {new Date(payment.period_start).toLocaleDateString('de-DE')} - {new Date(payment.period_end).toLocaleDateString('de-DE')}
                  </div>
                )}
                {payment.notes && (
                  <div className="payment-notes">{payment.notes}</div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default EmployeeEditor;
